import React, {
    useEffect,
    useState,
    useContext
} from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Skeleton from '@mui/material/Skeleton';
import {
    useParams,
    useNavigate,
    useMatch,
    Link
} from 'react-router';
import { get, ref, set, onChildChanged } from 'firebase/database';
import CrudContext from './crud-context';
import FormField from './form-field';
import ModelTypes from './model-types';
import AppContext from '../app-context';
import { PageContainer } from '@toolpad/core/PageContainer';
import { useFirebase } from '../firebase-context';

const shallowEqual = (objA, objB) => {
    if (!objA || !objB) {
      return objA === objB
    }
    return !Boolean(
      Object
        .keys(Object.assign({}, objA, objB))
        .find((key) => objA[key] !== objB[key])
    )
};

const NewItemQualifierSelection = () => {
    const navigate = useNavigate();
    let { id } = useParams();

    const {
        formState: { qualifier }
    } = useContext(CrudContext);

    const okToSubmit = qualifier;
    function continueWithSelection() {
      navigate(`/places/${qualifier}%2F${id}`);
    }
    return (<div>
        <FormField
            name="qualifier"
        />
        <Button disabled={ !okToSubmit } onClick={continueWithSelection} color="primary">
            Continue
        </Button>
    </div>);
}

const EditItem = ({ definition }) => {
    let { id } = useParams();
    const {
        modelType,
        hardSetFormState,
        hardSetEntityState,
        formState,
        entityState,
        storageKey
    } = useContext(CrudContext);
    const {
        db,
        user
    } = useFirebase();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const { uid } = user;
    const pseudoLinked = !id;
    if (pseudoLinked) id = uid;

    const cruddyIdForUri = id.replace('%2F', '/');
    const [userHasAccess, setUserHasAccess] = useState(true);

    const {
        accessLevel
    } = useContext(AppContext);
    const idIsQualified = modelType !== ModelTypes.USEROWNEDLIST || id.indexOf('%2F') > -1;

    const [fbRef, setFbRef] = useState();

    function goBack() {
        navigate('../');
    }

    function handleClose() {
        setOpen(false);
    }

    function openModal() {
        setOpen(true);
    }

    function deleteItem() {
        handleClose();
        set(
            ref(db, fbRef),
            null
        ).then(() => {
            goBack();
        });
        // toast the delete
    }

    function archiveItem() {
        handleClose();
        // copy to archived
        const archiveRef = `/${storageKey}-archive/${cruddyIdForUri}`;
        set(ref(db, archiveRef), formState);
        // remove from open
        set(ref(db, fbRef), null).then(() => {
            goBack();
        });
    }

    useEffect(() => {
        const importNewItemInfo = (info) => {
            hardSetFormState(info);
            hardSetEntityState(info);
        };
        // setCrumbs([{ url: './', label: `${crudLabel}s` }, { url: '', label: 'This Item' }]);
        
        const newRef = `/${storageKey}/${cruddyIdForUri}`;
        onChildChanged(
            ref(db, newRef),
            (snapshot) => {
                console.log('something changed externally');
            }
        )

        get(ref(db, newRef)).then((snapshot) => {
            setUserHasAccess(true);
            importNewItemInfo(snapshot.val() || {});
        }).catch((e) => {
            setUserHasAccess(false);
        });
        setFbRef(newRef);
        // const refPath = `/${storageKey}/${cruddyIdForUri}`;
        // let newRef = firebase.database().ref(refPath);
        // newRef.onDisconnect().cancel();
        // newRef.on('child_changed', (snapshot) => {
        //    console.log('something changed externally');
        //    // toast external change
        // });
        // newRef.once('value')
        //     .then((snapshot) => { setUserHasAccess(true); importNewItemInfo(snapshot.val() || {}); })
        //     .catch(() => { setUserHasAccess(false); });
        // return () => newRef.off();
        return () => { hardSetFormState({}); }
    }, [id, accessLevel, uid]); // eslint-disable-line

    function resetForm() {
        hardSetFormState(entityState);
    }

    function onSuccessfulSave() {
        // toast a success
        hardSetEntityState(formState);
        navigate('../');
    }

    function onFailedSave(firebaseResponse) {
        // pluck the error message
        // toast the failure
    }

    function onSaveResponse(resp) {
        if (resp === undefined) {
            onSuccessfulSave();
        } else {
            onFailedSave(resp);
        } 
    }

    function saveItem() {
        set(ref(db, fbRef), formState)
            .then(onSaveResponse);
    }

    if (!userHasAccess) {
        return (<div>You are not authorized to access this item</div>);
    }

    if (!entityState || entityState == {}){
        return (<div>LOADING</div>);
    }

    if (!idIsQualified) {
        return (<NewItemQualifierSelection />);
    }

    const isSavable = !shallowEqual(formState, entityState);

    return (<CrudContext.Consumer>{ (props) => {
        const { fields = [], crudLabel = 'Item', labelAccessor } = props;

        return (
            <div>
                { !pseudoLinked && (<Button onClick={goBack} color="secondary">Back to List</Button>) }
                <Card  style={ { overflowX: 'auto', maxWidth: '80vw', padding: 12}}>
                    <CardContent>
                        <Typography variant="h5">{formState[labelAccessor] || <Skeleton width="20vw" height={ 36 } />}</Typography>
                        <Typography variant="subtitle1">{ crudLabel }</Typography>
                        <div style={ { padding: 12, width: 400, margin: 'auto', display: 'flex', flexDirection: 'column' } }>
                            { Object.keys(fields).filter(o => o !== 'qualifier').map((fieldName) => (
                                <div key={ fieldName } style={ { width: 400, marginBottom: 12 } }>
                                    <FormField
                                        name={ fieldName }
                                        style={ { width: '100%' } }
                                    />
                                </div>
                            )) }
                        </div>
                        <CardActions style={{ width: '100%', textAlign: 'right' }}>
                            <IconButton
                                aria-label="Remove Item"
                                onClick={ openModal }
                            >
                                <DeleteIcon />
                            </IconButton>
                            <IconButton
                                aria-label="Archive Item"
                                style={ { marginLeft: '32px',marginRight: 'auto' } }
                                onClick={ archiveItem }
                            >
                                <ArchiveIcon />
                            </IconButton>
                            <Button disabled={ !isSavable } onClick={ resetForm } size="small" color="secondary">
                                Discard Changes
                            </Button>
                            <Button disabled={ !isSavable } onClick={ saveItem } size="small" color="secondary" variant="contained">
                                Save
                            </Button>
                        </CardActions>
                    </CardContent>
                </Card>
                <Dialog
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Delete this item?"}</DialogTitle>
                    <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Permanently remove this item from the database. This operation cannot be cancelled or undone.
                    </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={deleteItem} variant="contained" color="primary" autoFocus>
                        DELETE
                    </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    } }</CrudContext.Consumer>);
};

export default EditItem;
